// File: frontend/src/services/apolloClient.js

import { ApolloClient, InMemoryCache, split, HttpLink, from } from "@apollo/client";
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { getMainDefinition } from "@apollo/client/utilities";

// 1) HTTP link for queries & mutations
const httpLink = new HttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URL || "http://localhost:8000/graphql/",
  credentials: "include",
});

// 2) Auth link with CSRF protection
const authLink = setContext(async (_, { headers }) => {
  // Get CSRF token from cookie for GraphQL mutations
  const getCsrfToken = () => {
    const name = 'csrftoken';
    const value = "; " + document.cookie;
    const parts = value.split("; " + name + "=");
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  const csrfToken = getCsrfToken();
  
  return {
    headers: {
      ...headers,
      ...(csrfToken && { 'X-CSRFToken': csrfToken }),
    }
  };
});

// 3) WebSocket link for subscriptions with enhanced connection handling
const wsClient = createClient({
  url: process.env.REACT_APP_GRAPHQL_WS_URL || "ws://localhost:8000/graphql/",
  connectionParams: {
    credentials: "include",
  },
  retryAttempts: 5,
  retryWait: async (retries) => {
    const delay = Math.min(1000 * 2 ** retries, 10000);
    await new Promise(resolve => setTimeout(resolve, delay));
  },
  on: {
    error: (error) => {
      console.error('WebSocket error:', error);
    },
  },
  keepAlive: 10000,
});

const wsLink = new GraphQLWsLink(wsClient);

// 4) Combine auth link with HTTP link
const authenticatedHttpLink = from([authLink, httpLink]);

// 5) Split by operation type: subscriptions go over WS, everything else over HTTP
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  authenticatedHttpLink
);

// 6) Create the Apollo Client with enhanced security
const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

export default client;